/**
 * One preparation step's frame (f10.3): number, name, status, summary, the
 * server's acceptance blockers and the Accept / Apply update buttons.
 *
 * Buttons render only from the server's `capabilities`; no rule is
 * re-derived here. The body region is filled by the step's own feature
 * (f10.4-f10.9, f14.3) through `STEP_BODIES`.
 */

import type { ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Download } from 'lucide-react';

import { ApiError, type CycleWorkflowStep } from '@/api/client';
import { useAcceptCycleStep } from '@/api/queries';
import { NotFound } from '@/routes/NotFound';

import { useCycleWorkspace } from './CycleWorkspaceLayout';
import { ActualsBlockerLinks, ActualsStep } from './steps/actuals/ActualsStep';
import { isPendingVarianceBlocker } from './steps/actuals/actualsStepView';
import { BillableDaysStep } from './steps/billableDays/BillableDaysStep';
import { SalesRatesStep } from './steps/cosmaSalesRates/SalesRatesStep';
import { ExchangeRatesStep } from './steps/exchangeRates/ExchangeRatesStep';
import { KimbleDumpStep } from './steps/kimbleDump/KimbleDumpStep';
import { ReleaseStep } from './steps/release/ReleaseStep';
import { UsEngagementsStep } from './steps/usEngagements/UsEngagementsStep';
import { stepMeta, stepPillClass } from './workflowView';

export interface StepBodyProps {
  cycleId: string;
  step: CycleWorkflowStep;
}

/** Step code -> the feature's body component. f10.4-f10.9 each add one line. */
export const STEP_BODIES: Readonly<Record<string, ComponentType<StepBodyProps>>> = {
  US_ENGAGEMENTS: UsEngagementsStep,
  BILLABLE_DAYS: BillableDaysStep,
  KIMBLE_DUMP: KimbleDumpStep,
  COSMA_SALES_RATES: SalesRatesStep,
  EXCHANGE_RATES: ExchangeRatesStep,
  RELEASE_FOR_INPUT: ReleaseStep,
  ACTUALS: ActualsStep,
};

function acceptFailure(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.reason === 'CYCLE_STEP_VERSION_CONFLICT') {
    return 'This step changed — the latest state is shown.';
  }
  return error instanceof Error ? error.message : 'The step could not be accepted.';
}

/** Keyed by step, so a refusal on one step never lingers on the next. */
export function CycleStepPage() {
  const { stepCode = '' } = useParams();
  return <StepFrame key={stepCode} stepCode={stepCode} />;
}

function StepFrame({ stepCode }: { stepCode: string }) {
  const { cycle, workflow, requestApply } = useCycleWorkspace();
  const accept = useAcceptCycleStep(cycle.id);

  const step = workflow.steps.find((item) => item.code === stepCode);
  if (!step) return <NotFound />;

  const Body = STEP_BODIES[step.code];
  const failure = acceptFailure(accept.error);
  const blockers =
    accept.error instanceof ApiError && accept.error.reason === 'CYCLE_STEP_NOT_READY'
      ? accept.error.errors.map((item) => item.message)
      : step.acceptance_blockers;

  return (
    <div className="card cycle-step-frame">
      <div className="card-head">
        <div className="cycle-step-heading">
          <span className="cycle-step-number mono">Step {step.number}</span>
          <h3>{step.name}</h3>
        </div>
        <span className={stepPillClass(step.status.code)}>{step.status.name}</span>
      </div>
      <div className="card-body stack">
        <div className="hint">{stepMeta(step, workflow)}</div>

        {failure && !blockers.length && (
          <div className="alert alert-danger" role="alert">
            <div>
              <div className="a-body">{failure}</div>
            </div>
          </div>
        )}

        {blockers.length > 0 && (
          <div className="alert alert-warning" role="status">
            <div>
              <div className="a-title">Not ready to accept</div>
              <ul className="cycle-step-blockers">
                {blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
              {step.code === 'ACTUALS' && blockers.some(isPendingVarianceBlocker) && (
                <ActualsBlockerLinks cycleId={cycle.id} />
              )}
            </div>
          </div>
        )}

        {Body ? (
          <Body cycleId={cycle.id} step={step} />
        ) : step.code === 'RELEASE_FOR_INPUT' ? (
          <p className="hint">
            This step completes when the cycle is released to managers for input.
          </p>
        ) : null}
      </div>
      {(step.capabilities.can_accept || step.capabilities.can_apply_update) && (
        <div className="card-foot cycle-step-actions">
          {step.capabilities.can_apply_update && (
            <button type="button" className="btn btn-secondary" onClick={() => requestApply(step)}>
              <Download aria-hidden="true" /> Apply update
            </button>
          )}
          {step.capabilities.can_accept && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={accept.isPending}
              onClick={() =>
                accept.mutate({ stepCode: step.code, body: { expected_version: step.version } })
              }
            >
              <Check aria-hidden="true" /> Accept step
            </button>
          )}
        </div>
      )}
    </div>
  );
}
